import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { RefreshIntervalOption } from '../config/plotTypes';

// -- type definitions --

export type RefreshInterval = number;
type RefreshSelectorValue = string;

// -- constants --

const DEFAULT_REFRESH_OPTIONS: RefreshIntervalOption[] = [
  { quantity: 1, unit: 'minute' },
  { quantity: 2, unit: 'minute' },
  { quantity: 5, unit: 'minute' },
  { quantity: 10, unit: 'minute' },
];

const unitToMilliseconds: Record<RefreshIntervalOption['unit'], number> = {
  second: 1_000,
  minute: 60_000,
  hour: 60 * 60_000,
  day: 24 * 60 * 60_000,
};

const unitLabels: Record<RefreshIntervalOption['unit'], [string, string]> = {
  second: ['seconde', 'secondes'],
  minute: ['minute', 'minutes'],
  hour: ['heure', 'heures'],
  day: ['jour', 'jours'],
};

// -- functions --

const optionKey = (option: RefreshIntervalOption): string =>
  `${option.quantity}:${option.unit}`;

const sameOption = (
  left?: RefreshIntervalOption,
  right?: RefreshIntervalOption
): boolean => {
  if (!left || !right) {
    return left === right;
  }

  return optionKey(left) === optionKey(right);
};

export const refreshIntervalOptionToMs = (
  option: RefreshIntervalOption
): RefreshInterval => option.quantity * unitToMilliseconds[option.unit];

const formatRefreshOptionLabel = (option: RefreshIntervalOption): string => {
  const [singular, plural] = unitLabels[option.unit];
  return `${option.quantity} ${option.quantity > 1 ? plural : singular}`;
};

const normalizeOptions = (
  options?: RefreshIntervalOption[]
): RefreshIntervalOption[] => {
  const resolvedOptions = options ?? DEFAULT_REFRESH_OPTIONS;
  const seen = new Set<string>();

  return resolvedOptions.reduce<RefreshIntervalOption[]>((acc, option) => {
    const normalizedOption = {
      quantity: Math.max(1, Math.round(option.quantity)),
      unit: option.unit,
    };
    const key = optionKey(normalizedOption);

    if (!seen.has(key)) {
      seen.add(key);
      acc.push(normalizedOption);
    }

    return acc;
  }, []);
};

// -- main component --

export interface RefreshIntervalSelectorProps {
  value?: RefreshIntervalOption;
  options?: RefreshIntervalOption[];
  onIntervalChange: (option: RefreshIntervalOption) => void;
  onStop?: () => void;
}

export const RefreshIntervalSelector: React.FC<
  RefreshIntervalSelectorProps
> = ({ value, options, onIntervalChange, onStop }) => {
  const resolvedOptions = useMemo(() => normalizeOptions(options), [options]);
  const [selectedInterval, setSelectedInterval] =
    useState<RefreshSelectorValue>(
      value === undefined ? 'none' : optionKey(value)
    );
  const lastEmittedValueRef = useRef<RefreshIntervalOption | undefined>(
    undefined
  );

  useEffect(() => {
    setSelectedInterval(value === undefined ? 'none' : optionKey(value));
  }, [value]);

  useEffect(() => {
    if (!value || sameOption(lastEmittedValueRef.current, value)) {
      return;
    }

    lastEmittedValueRef.current = value;
    onIntervalChange(value);
  }, [onIntervalChange, value]);

  const handleChange = (event: SelectChangeEvent<RefreshSelectorValue>) => {
    const rawValue = event.target.value;

    if (rawValue === 'none') {
      setSelectedInterval('none');
      lastEmittedValueRef.current = undefined;
      onStop?.();
      return;
    }

    const selectedOption = resolvedOptions.find(
      (option) => optionKey(option) === rawValue
    );

    if (!selectedOption) {
      return;
    }

    if (sameOption(lastEmittedValueRef.current, selectedOption)) {
      setSelectedInterval(rawValue);
      return;
    }

    setSelectedInterval(rawValue);
    lastEmittedValueRef.current = selectedOption;
    onIntervalChange(selectedOption);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <InputLabel>Rafraîchissement</InputLabel>
      <Select
        label="Rafraîchissement"
        value={selectedInterval}
        onChange={handleChange}
      >
        <MenuItem value="none">Aucun</MenuItem>
        {resolvedOptions.map((option) => (
          <MenuItem key={optionKey(option)} value={optionKey(option)}>
            {formatRefreshOptionLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
